import React, { createContext, useContext, useEffect, useState } from 'react';
import { useAuth } from './AuthContext';
import api from '../config/api';
import { Star } from 'lucide-react';

const NotificationContext = createContext();

export function NotificationProvider({ children }) {
  const { user } = useAuth();
  const [toast, setToast] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [isBellOpen, setIsBellOpen] = useState(false);

  useEffect(() => {
    if (!user) return;

    // Fetch initial notifications from DB
    const fetchHistory = async () => {
      try {
        const res = await api.get('/notifications');
        if (res.data.success) {
          // Format them to match SSE structure
          const history = res.data.data.map(n => ({
            title: n.title,
            message: n.message,
            isVip: n.is_vip,
            id: n.id,
            read: n.is_read,
            time: new Date(n.created_at)
          }));
          setNotifications(history);
        }
      } catch (err) {
        console.error('Failed to load notifications history', err);
      }
    };
    fetchHistory();

    const sseUrl = `${process.env.REACT_APP_API_URL || 'http://localhost:4000/api'}/notifications/stream`;
    const eventSource = new EventSource(sseUrl, { withCredentials: true });

    eventSource.onmessage = (event) => {
      try {
        const payload = JSON.parse(event.data);
        if (payload.type === 'VISITOR_ENTRY') {
          const isTargetRole = payload.targetRoles?.includes(user.role);
          const isTargetUser = payload.targetUserIds?.includes(user.userId);
          
          if (isTargetRole || isTargetUser) {
            const newNotif = {
              title: 'Entrée Visiteur',
              message: `${payload.data.visitorName} vient d'arriver à l'accueil.`,
              isVip: payload.data.isVip,
              id: payload.dbNotification ? payload.dbNotification.id : Date.now(),
              read: false,
              time: new Date()
            };
            
            setToast(newNotif);
            setNotifications(prev => {
              // Avoid duplicates if SSE reconnects or sends same ID
              if (prev.find(n => n.id === newNotif.id)) return prev;
              return [newNotif, ...prev];
            });
          }
        }
      } catch (err) {
        console.error('Error parsing SSE:', err);
      }
    };

    eventSource.onerror = (err) => {
      console.error('SSE Error:', err);
    };

    return () => {
      eventSource.close();
    };
  }, [user]);

  useEffect(() => {
    if (toast) {
      const timer = setTimeout(() => setToast(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [toast]);

  const markAllAsRead = () => {
    setNotifications(prev => prev.map(n => ({ ...n, read: true })));
  };

  const deleteNotification = async (id) => {
    try {
      await api.delete(`/notifications/${id}`);
      setNotifications(prev => prev.filter(n => n.id !== id));
    } catch (err) {
      console.error('Failed to delete notification', err);
    }
  };

  const unreadCount = notifications.filter(n => !n.read).length;

  return (
    <NotificationContext.Provider value={{ toast, notifications, unreadCount, markAllAsRead, isBellOpen, setIsBellOpen, deleteNotification }}>
      {children}
      {toast && (
        <div style={{
          position: 'fixed',
          top: '20px',
          right: '20px',
          backgroundColor: toast.isVip ? '#fef08a' : '#ffffff',
          borderLeft: `4px solid ${toast.isVip ? '#ca8a04' : '#2563eb'}`,
          padding: '16px 24px',
          borderRadius: '8px',
          boxShadow: '0 10px 25px rgba(0,0,0,0.1)',
          zIndex: 9999,
          animation: 'slideInRight 0.3s ease-out forwards',
          display: 'flex',
          flexDirection: 'column',
          gap: '4px'
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <strong style={{ color: '#1e293b', fontSize: '14px' }}>
              {toast.isVip ? <><Star size={14} style={{marginRight: '4px', verticalAlign: 'text-bottom'}} /> VIP Arrivé</> : toast.title}
            </strong>
            <button 
              onClick={() => setToast(null)}
              style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '18px', color: '#64748b' }}
            >
              &times;
            </button>
          </div>
          <span style={{ color: '#475569', fontSize: '14px' }}>{toast.message}</span>
        </div>
      )}
      <style dangerouslySetInnerHTML={{__html: `
        @keyframes slideInRight {
          from { transform: translateX(100%); opacity: 0; }
          to { transform: translateX(0); opacity: 1; }
        }
      `}} />
    </NotificationContext.Provider>
  );
}

export function useNotification() {
  return useContext(NotificationContext);
}
